import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import {
    currentStateText,
    reasonText,
    serialStatusPresentation,
} from "./workOrderHelpers";
import { STATUS_BODY_INK, STATUS_PALETTE, STATUS_SURFACE } from "../statusPalette";
import { FORM_TOKENS } from "./WorkOrderFormShared";

// The dry-run verdict as it reads IN THE RAIL.
//
// WHY NOT SerialReportTable. That component is a five-column table, and it is
// right for the two places it still serves -- the failed submit and the
// refused approval -- which both render full width in a dialog. Dropped into a
// 440px rail the same markup produces a horizontal scrollbar with the Reason
// column, the one thing the user has to read, parked off the right edge.
//
// So the rail gets a LIST, not a table: one row per serial, the verdict pinned
// to the right, and the reason on its own line where it can wrap to as many
// lines as it needs. Nothing scrolls sideways, and the column that mattered
// most is the one with the most room.
//
// Both renderings read the same report and the same three helpers
// (serialStatusPresentation, currentStateText, reasonText), so a verdict
// cannot say one thing here and another in the dialog.

// How many rows render before the rest go behind a toggle. A fifty-serial
// paste that fails wholesale must not turn the rail into a mile of scroll.
const COLLAPSED_ROW_COUNT = 12;

const StatusPill = ({ status, label }) => {
    const { theme } = serialStatusPresentation(status);
    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                px: 1,
                py: "3px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                color: theme.color,
                backgroundColor: theme.background,
            }}
        >
            {label}
        </Box>
    );
};

// ONLY THE COUNTS THAT ARE NOT ZERO. The full set -- submitted, ready, not
// allowed, to be registered, repeated -- wrapped onto three lines in the rail
// and made "0 repeated" as prominent as "3 not allowed". A count of zero is
// the absence of a thing, and it does not need a chip to say so.
const CountChip = ({ label, value, tone }) => (
    <Box
        sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: "6px",
            border: `1px solid ${STATUS_PALETTE[tone].border}`,
            backgroundColor: STATUS_PALETTE[tone].bg,
            fontSize: "13px",
            fontWeight: 500,
            color: STATUS_PALETTE[tone].fg,
            whiteSpace: "nowrap",
        }}
    >
        {value} {label}
    </Box>
);

const PreviewSerialList = ({
    report,
    // Dropping the refused serials from the box. Withheld in two cases, for
    // two different reasons -- see below.
    onRemoveSerials = null,
    // STALE: the box no longer matches this answer, so it is greyed and the
    // remove action is withheld. Removing "these serials" against an
    // out-of-date verdict would strip the wrong ones.
    dimmed = false,
    testIdPrefix = "work-order-serial-report",
}) => {
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        setShowAll(false);
    }, [report]);

    // Problems first. The eligible rows stay -- they are how the maker sees
    // that forty-eight serials were fine and two need touching -- but they are
    // never what has to be scrolled past to reach the failures.
    const orderedRows = useMemo(() => {
        const rows = report?.rows || [];
        return [
            ...rows.filter((r) => r.isProblem),
            ...rows.filter((r) => !r.isProblem),
        ];
    }, [report]);

    if (!report || !orderedRows.length) return null;

    const { summary, problemSerials } = report;
    const problems = problemSerials?.length || 0;
    const warnings = Array.isArray(report.warnings) ? report.warnings : [];
    const visibleRows = showAll
        ? orderedRows
        : orderedRows.slice(0, COLLAPSED_ROW_COUNT);

    // REMOVING EVERY ROW IS NOT A FIX, it is an empty form. The button offers
    // to drop the refused serials and keep the rest; with nothing in the rest
    // it would clear the box and leave the user where they started, having
    // lost what they typed. When all of them are refused the only way forward
    // is to correct them, so the button is not offered.
    const keptCount = orderedRows.length - problems;
    const canRemove = Boolean(onRemoveSerials) && !dimmed && problems > 0 && keptCount > 0;

    return (
        <Box
            data-testid={testIdPrefix}
            data-stale={dimmed ? "true" : "false"}
            sx={{
                opacity: dimmed ? 0.55 : 1,
                filter: dimmed ? "grayscale(1)" : "none",
            }}
        >
            {/* The counts, and only the ones that happened. */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {summary?.eligible > 0 && (
                    <CountChip label="ready" value={summary.eligible} tone="success" />
                )}
                {summary?.ineligible > 0 && (
                    <CountChip label="not allowed" value={summary.ineligible} tone="error" />
                )}
                {/* Never in the error tone. The server emits `new` only on a
                    movement type that registers what it moves, so an unknown
                    serial here is the job rather than a typo. */}
                {summary?.new > 0 && (
                    <CountChip label="to be registered" value={summary.new} tone="info" />
                )}
                {summary?.duplicate > 0 && (
                    <CountChip label="repeated" value={summary.duplicate} tone="error" />
                )}
                {!problems && !summary?.new && (
                    <Typography
                        data-testid={`${testIdPrefix}-clear`}
                        sx={{ fontSize: "13px", color: FORM_TOKENS.inkLabel, alignSelf: "center" }}
                    >
                        nothing blocked
                    </Typography>
                )}
            </Box>

            {/* ADVISORY, and never a blocker. The commonest one says the
                serials sit at several different origins, which is allowed -- a
                consolidation return legitimately pulls from four sub-dealers --
                and reading it as a failure is the mistake this separate
                styling exists to prevent. */}
            {warnings.length > 0 && (
                <Box
                    data-testid={`${testIdPrefix}-warnings`}
                    sx={{
                        mt: 1.5,
                        border: `1px solid ${STATUS_PALETTE.warningSoft.border}`,
                        backgroundColor: STATUS_SURFACE.warning,
                        borderRadius: "6px",
                        px: 1.5,
                        py: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: STATUS_PALETTE.warning.fg,
                            mb: 0.25,
                        }}
                    >
                        Worth checking, but nothing is blocked
                    </Typography>
                    {warnings.map((warning, index) => (
                        <Typography
                            key={`${warning}-${index}`}
                            data-testid={`${testIdPrefix}-warning-${index}`}
                            sx={{
                                fontSize: "12px",
                                color: STATUS_BODY_INK.warning,
                                lineHeight: "18px",
                            }}
                        >
                            {warning}
                        </Typography>
                    ))}
                </Box>
            )}

            {/* ABOVE THE ROWS, not under them. It acts on what the rows say,
                and a fifty-serial report would otherwise hide its own remedy
                below a screen of scrolling. */}
            {canRemove && (
                <Box
                    sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.25,
                    }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        data-testid={`${testIdPrefix}-remove`}
                        onClick={() => onRemoveSerials(problemSerials)}
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "8px",
                            px: 1.75,
                            borderColor: FORM_TOKENS.fieldBorder,
                            color: FORM_TOKENS.ink,
                            backgroundColor: FORM_TOKENS.surface,
                            "&:hover": { borderColor: FORM_TOKENS.ink },
                        }}
                    >
                        Remove these {problems}
                    </Button>
                    <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel }}>
                        the rest of the form is kept
                    </Typography>
                </Box>
            )}

            {/* One row per serial. FULL BLEED past the band's padding, so the
                rules between rows run edge to edge the way they do in every
                other list in the product. */}
            <Box sx={{ mt: 2, mx: -3 }}>
                {visibleRows.map((row, index) => (
                    <Box
                        key={`${row.serialNumber}-${index}`}
                        data-testid={`${testIdPrefix}-row-${row.serialNumber}`}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderTop: `1px solid ${FORM_TOKENS.bandRule}`,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                    color: FORM_TOKENS.ink,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {row.serialNumber || "-"}
                            </Typography>
                            {/* THE REASON GETS THE WIDTH, because it is the
                                only cell that tells the user what to do. A
                                refused row says why; an accepted one says
                                where the asset is now, which is how the maker
                                confirms they typed the serial they meant. */}
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    lineHeight: "18px",
                                    mt: 0.25,
                                    color: row.isProblem
                                        ? FORM_TOKENS.danger
                                        : FORM_TOKENS.inkLabel,
                                }}
                            >
                                {row.isProblem ? reasonText(row) : currentStateText(row)}
                            </Typography>
                        </Box>
                        <StatusPill status={row.status} label={row.statusLabel} />
                    </Box>
                ))}
            </Box>

            {orderedRows.length > COLLAPSED_ROW_COUNT && (
                <Button
                    size="small"
                    data-testid={`${testIdPrefix}-toggle`}
                    onClick={() => setShowAll((prev) => !prev)}
                    sx={{
                        mt: 1,
                        fontSize: 12,
                        textTransform: "none",
                        color: FORM_TOKENS.purple,
                    }}
                >
                    {showAll ? "Show fewer" : `Show all ${orderedRows.length} serials`}
                </Button>
            )}
        </Box>
    );
};

export default PreviewSerialList;
